import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Zap,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Upload,
  CloudUpload,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import { loginWithGoogle } from "../../app/features/auth";
import { canPerformAnalysis } from "../../services/analysisLimitService";
import Button from "../ui/Button";
import { cn } from "../../lib/utils";

const HeroSection = ({ tweetUrl, setTweetUrl }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [timeUntilReset, setTimeUntilReset] = useState(null);
  const [checkingLimit, setCheckingLimit] = useState(false);
  const { session, user } = useSelector((state) => state.auth);
  const { isLoading, error } = useSelector((state) => state.tweets);

  const handleFileSelect = useCallback(
    async (file) => {
      if (file) {
        setSelectedFile(file);
        setTweetUrl(URL.createObjectURL(file));
        setLimitReached(false);
        setTimeUntilReset(null);

        // Check limit immediately after file selection
        const isSignedIn = !!session && !!user;
        if (!isSignedIn) {
          setCheckingLimit(true);
          try {
            const limitCheck = await canPerformAnalysis(isSignedIn);
            if (!limitCheck.canAnalyze) {
              setLimitReached(true);
              setTimeUntilReset(limitCheck.timeUntilReset || null);
              setCheckingLimit(false);
              return; // Don't proceed if limit reached
            }
          } catch (err) {
            console.error("Error checking limit:", err);
            // Fail open - allow if check fails
          } finally {
            setCheckingLimit(false);
          }
        }
      }
    },
    [session, user, setTweetUrl]
  );

  const handleGenerate = useCallback(async () => {
    if (!selectedFile) {
      return;
    }

    // Check limit again before navigating (in case user is not signed in)
    const isSignedIn = !!session && !!user;
    if (!isSignedIn) {
      setCheckingLimit(true);
      try {
        const limitCheck = await canPerformAnalysis(isSignedIn);
        if (!limitCheck.canAnalyze) {
          setLimitReached(true);
          setTimeUntilReset(limitCheck.timeUntilReset || null);
          setCheckingLimit(false);
          return; // Don't navigate if limit reached
        }
      } catch (err) {
        console.error("Error checking limit:", err);
        // Fail open - allow if check fails
      } finally {
        setCheckingLimit(false);
      }
    }

    // Navigate to personality analysis page
    navigate("/personality-analysis", { state: { imageFile: selectedFile } });
  }, [selectedFile, session, user, navigate]);

  // Automatically trigger analysis when file is selected
  useEffect(() => {
    if (selectedFile && !limitReached && !checkingLimit && !isLoading) {
      // Small delay to ensure all state is updated
      const timer = setTimeout(() => {
        handleGenerate();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [selectedFile, limitReached, checkingLimit, isLoading, handleGenerate]);

  const onDrop = useCallback(
    (acceptedFiles, fileRejections) => {
      if (fileRejections && fileRejections.length > 0) {
        // Handle file rejection (wrong type, too large, etc.)
        return;
      }
      const file = acceptedFiles?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
      "image/heic": [".heic"],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB
    disabled: isLoading,
  });

  const handleSignIn = async () => {
    try {
      await dispatch(loginWithGoogle()).unwrap();
    } catch (error) {
      console.error("Error signing in:", error);
    }
  };

  return (
    <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 overflow-hidden">
      {/* Unique Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-orange-500/8 rounded-full blur-[140px]"></div>
      </div>

      <div className="max-w-7xl mx-auto relative">
        {/* Centered Content with Unique Layout */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-teal-500/10 via-rose-500/10 to-orange-500/10 border border-teal-500/20 mb-6">
            <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></div>
            <span className="text-xs sm:text-sm font-medium text-teal-400">
              Upload Image • AI Analysis • Get Feedback
            </span>
            <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse"></div>
          </div>

          <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-tight text-white mb-6 leading-[0.95] break-words">
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-orange-500 to-teal-500 animate-gradient hero-text-2">
              Get Personality Feedback
            </span>
            <span className="block hero-text-3">Powered by AI</span>
          </h1>

          <p className="text-lg sm:text-xl text-zinc-300 mb-10 max-w-2xl mx-auto leading-relaxed font-light hero-description">
            Discover insights about your personality through AI-powered image
            analysis. Get detailed feedback in seconds, no expertise needed.
          </p>

          {/* Bordered Dropzone Design */}
          <div className="max-w-2xl mx-auto mb-10">
            <div className="space-y-4">
              {/* Dropzone Area */}
              <div
                {...getRootProps()}
                className={cn(
                  "relative border-2 border-dashed rounded-lg p-12 bg-stone-900/50 cursor-pointer transition-all",
                  isDragActive
                    ? "border-rose-500 bg-gradient-to-r from-rose-500/10 via-orange-500/10 to-teal-500/10"
                    : "border-stone-700 hover:border-rose-500/50"
                )}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center justify-center gap-4">
                  <div
                    className={cn(
                      "transition-transform",
                      isDragActive && "scale-110"
                    )}
                  >
                    <CloudUpload
                      className={cn(
                        "w-16 h-16 transition-colors",
                        isDragActive ? "text-teal-400" : "text-teal-500"
                      )}
                    />
                  </div>
                  <div className="text-center space-y-2">
                    <p
                      className={cn(
                        "text-base font-medium transition-colors",
                        isDragActive
                          ? "text-transparent bg-clip-text bg-gradient-to-r from-rose-400 via-orange-500 to-teal-400"
                          : "text-transparent bg-clip-text bg-gradient-to-r from-rose-500 via-orange-500 to-teal-500"
                      )}
                    >
                      {isDragActive
                        ? "Drop your image here..."
                        : "Drag and Drop Supporting Files here to Upload."}
                    </p>
                    {!isDragActive && !selectedFile && (
                      <button
                        type="button"
                        className="px-6 py-2.5 bg-stone-800 hover:bg-stone-700 text-zinc-300 rounded-lg text-sm font-medium transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Trigger file input click
                          const input = e.currentTarget
                            .closest('[role="button"]')
                            ?.querySelector('input[type="file"]');
                          input?.click();
                        }}
                      >
                        Or Click to Select
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* File Preview and Analyze Button */}
              {selectedFile && (
                <div className="flex flex-col sm:flex-row gap-4 items-center p-4 bg-stone-900/50 rounded-lg border border-stone-800">
                  <div className="flex-1 flex items-center gap-3 min-w-0">
                    <Upload className="w-5 h-5 text-transparent bg-clip-text bg-gradient-to-r from-rose-500 to-teal-500 flex-shrink-0" />
                    <span className="text-zinc-300 truncate text-sm">
                      {selectedFile.name}
                    </span>
                  </div>
                  <Button
                    size="lg"
                    style={{ color: "#000000" }}
                    className="h-12 px-8 bg-gradient-to-r from-rose-500 to-teal-500 hover:from-rose-400 hover:to-teal-400 text-white font-bold shadow-lg shadow-rose-500/25 hover:shadow-xl hover:shadow-rose-500/40 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleGenerate}
                    disabled={
                      isLoading ||
                      !selectedFile ||
                      limitReached ||
                      checkingLimit
                    }
                  >
                    {isLoading ? (
                      <Loader2
                        className="w-5 h-5 animate-spin"
                        style={{ color: "#ffffff" }}
                      />
                    ) : (
                      <>
                        <span style={{ color: "#ffffff" }}>Analyze Now</span>
                        <Zap
                          className="w-5 h-5 ml-2"
                          style={{ color: "#ffffff" }}
                        />
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
            {error && (
              <div className="mt-3 flex items-center gap-2 text-red-400 text-sm justify-center">
                <AlertCircle className="w-4 h-4" />
                <span>{error}</span>
              </div>
            )}
            {limitReached && (
              <div className="mt-4 p-6 bg-stone-900/90 border-2 border-rose-500/50 rounded-xl">
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 mb-4 rounded-full flex items-center justify-center bg-rose-600">
                    <AlertCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    Daily Limit Reached
                  </h3>
                  <p className="text-zinc-400 mb-4 text-sm">
                    You've used your free daily analysis. Sign in with Google to
                    get unlimited analyses!
                  </p>
                  {timeUntilReset && (
                    <p className="text-xs text-zinc-500 mb-4">
                      Your limit will reset in{" "}
                      {Math.floor(timeUntilReset / 60) > 0
                        ? `${Math.floor(timeUntilReset / 60)} hour${
                            Math.floor(timeUntilReset / 60) > 1 ? "s" : ""
                          } and `
                        : ""}
                      {timeUntilReset % 60} minute
                      {timeUntilReset % 60 !== 1 ? "s" : ""}
                    </p>
                  )}
                  <button
                    onClick={handleSignIn}
                    className="px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-teal-600 to-rose-600 hover:from-teal-700 hover:to-rose-700 text-white transition duration-300 shadow-xl flex items-center gap-2"
                  >
                    <Zap className="w-4 h-4" />
                    Sign in with Google for Unlimited Analyses
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 text-sm text-zinc-400">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span>10,000+ Active Users</span>
            </div>
            <div className="hidden sm:block w-1 h-1 rounded-full bg-zinc-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-teal-500"></div>
              <span>1M+ Images Analyzed</span>
            </div>
            <div className="hidden sm:block w-1 h-1 rounded-full bg-zinc-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-rose-500"></div>
              <span>99.9% Uptime</span>
            </div>
          </div>
        </div>

        {/* Hero Visual - Personality Feedback Preview */}
        <div className="relative mt-16 max-w-4xl mx-auto">
          <div className="relative group">
            {/* Animated Border Glow */}
            <div className="absolute -inset-4 bg-gradient-to-r from-rose-500/20 via-orange-500/20 to-teal-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition duration-500"></div>

            <div className="relative bg-stone-900/80 backdrop-blur-2xl border-2 border-stone-800 rounded-3xl p-6 sm:p-10 shadow-2xl">
              {/* Custom Header */}
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-800/50">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-red-500"></div>
                  <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-500 font-mono">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                  <span>live preview</span>
                </div>
              </div>

              {/* Personality Feedback Preview */}
              <div className="relative bg-white rounded-xl p-6 border border-zinc-200 shadow-lg">
                {/* Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-tr from-rose-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg">
                      AI
                    </div>
                    <div>
                      <h3 className="font-bold text-black text-lg">
                        AI Personality Analysis
                      </h3>
                      <p className="text-gray-500 text-sm">
                        Powered by Advanced AI
                      </p>
                    </div>
                  </div>
                </div>

                {/* Personality Traits */}
                <div className="space-y-4 mb-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">
                        Confidence
                      </span>
                      <span className="text-sm text-gray-500">85%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-rose-500 to-teal-500 h-2 rounded-full"
                        style={{ width: "85%" }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">
                        Openness
                      </span>
                      <span className="text-sm text-gray-500">72%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-orange-500 to-amber-500 h-2 rounded-full"
                        style={{ width: "72%" }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">
                        Creativity
                      </span>
                      <span className="text-sm text-gray-500">68%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-pink-500 to-rose-500 h-2 rounded-full"
                        style={{ width: "68%" }}
                      ></div>
                    </div>
                  </div>
                </div>

                {/* Feedback Summary */}
                <div className="pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Your image suggests a{" "}
                    <strong>confident and creative</strong> personality with
                    strong leadership qualities. The analysis indicates high
                    levels of openness to new experiences and innovative
                    thinking.
                  </p>
                </div>
              </div>

              {/* Floating Badge - Unique Position */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-br from-rose-500 to-teal-600 text-white px-5 py-2.5 rounded-full text-xs font-bold shadow-2xl shadow-rose-500/40 flex items-center gap-2 border-2 border-zinc-900">
                <CheckCircle2 className="w-4 h-4" />
                <span>AI Analysis Ready</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
